import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Users, Mail, Crown, Trash2, LogOut, Send, Activity, BarChart3, Check, X, ClipboardList, Bookmark } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { notify } from "../lib/notify";
import SEO from "../components/SEO";

export default function Team() {
  const [data, setData] = useState(null);
  const [dash, setDash] = useState(null);
  const [sharedAssessments, setSharedAssessments] = useState([]);
  const [sharedSearches, setSharedSearches] = useState([]);
  const [err, setErr] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/org");
      setData(r.data);
      setErr(null);
      const d = await api.get("/org/dashboard");
      setDash(d.data);
      api.get("/org/assessments").then(a => setSharedAssessments(a.data.assessments || [])).catch(() => {});
      api.get("/org/searches").then(s => setSharedSearches(s.data.searches || [])).catch(() => {});
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load team.");
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const r = await api.post("/org/invite", { email: inviteEmail.trim(), role: inviteRole });
      const url = `${window.location.origin}${r.data.accept_url}`;
      try { await navigator.clipboard.writeText(url); }
      catch (error) { console.warn("Clipboard write blocked:", error?.message); }
      notify.success(`Invite created. Link copied to clipboard.`);
      setInviteEmail("");
      load();
    } catch (e) { notify.error(e.response?.data?.detail || "Invite failed."); }
    finally { setInviting(false); }
  };

  const revokeInvite = async (token) => {
    if (!window.confirm("Revoke this invite?")) return;
    try { await api.delete(`/org/invite/${token}`); notify.success("Revoked."); load(); }
    catch { notify.error("Could not revoke."); }
  };

  const removeMember = async (uid, email) => {
    if (!window.confirm(`Remove ${email} from the team?`)) return;
    try { await api.delete(`/org/members/${uid}`); notify.success("Removed."); load(); }
    catch (e) { notify.error(e.response?.data?.detail || "Could not remove."); }
  };

  const leaveOrg = async () => {
    if (!window.confirm("Leave this team workspace?")) return;
    try {
      const r = await api.post("/org/leave");
      notify.success(r.data.org_deleted ? "Team deleted (you were the last member)." : "You left the team.");
      window.location.href = "/";
    } catch { notify.error("Could not leave."); }
  };

  if (!user) return <div className="container-rp section text-center"><h1 className="font-display text-3xl font-bold">Sign in to view your team</h1><Link to="/login?next=/team" className="btn-accent mt-6 inline-flex">Sign in</Link></div>;
  if (err) return <NoOrg err={err} />;
  if (!data) return <div className="container-rp section text-slate-400">Loading team…</div>;

  const isOwnerOrAdmin = data.org.my_role === "owner" || data.org.my_role === "admin";

  return (
    <div data-testid="page-team">
      <SEO title={`${data.org.name} · Team workspace`} description={`Team workspace for ${data.org.name} — shared workflows, assessments and Singapore risk briefings.`} path="/team" />

      <section className="border-b border-slate-200">
        <div className="container-rp py-12">
          <div className="overline">Team workspace · {data.org.plan}</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold mt-3 tracking-tight">{data.org.name}</h1>
          <p className="text-slate-600 mt-4 max-w-2xl">@{data.org.domain} · {data.org.member_count} {data.org.member_count === 1 ? "member" : "members"} · You are <strong>{data.org.my_role}</strong></p>
        </div>
      </section>

      <section className="section">
        <div className="container-rp grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Dashboard */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rp-card p-6" data-testid="team-dashboard">
              <div className="overline flex items-center gap-2"><BarChart3 size={11} /> Team activity</div>
              <h2 className="font-display text-2xl font-semibold mt-3">Pulse.</h2>
              {dash ? (
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <Stat label="Members" v={dash.members} />
                  <Stat label="Saved workflows" v={dash.workflows_saved} />
                  <Stat label="Total runs" v={dash.workflow_runs} />
                </div>
              ) : <div className="text-xs text-slate-400 mt-4">Loading…</div>}

              {dash?.tool_averages?.length > 0 && (
                <div className="mt-8">
                  <div className="overline-muted">Team average scores</div>
                  <div className="space-y-2 mt-3">
                    {dash.tool_averages.map(t => (
                      <div key={t.tool_id} className="flex items-center gap-3 text-sm">
                        <span className="font-mono text-xs text-slate-500 flex-1">{t.tool_id}</span>
                        <span className="font-display text-lg font-bold" style={{ color: "var(--rp-accent)" }}>{t.avg_score}</span>
                        <span className="text-[10px] text-slate-400">({t.members_scored} scored)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="rp-card p-6">
              <div className="overline flex items-center gap-2"><Activity size={11} /> Recent team runs</div>
              <h3 className="font-display text-lg font-semibold mt-3">Last 10 workflow runs (team-visible).</h3>
              <div className="mt-4 space-y-2">
                {dash?.recent_runs?.length > 0 ? dash.recent_runs.map(r => (
                  <Link key={r.run_id} to={`/workflows/runs/${r.run_id}`} className="block p-3 hover:bg-slate-50 rounded-sm border border-slate-100" data-testid={`team-run-${r.run_id}`}>
                    <div className="text-sm font-medium">{r.result?.template_name || r.template_id}</div>
                    <div className="text-xs text-slate-500 mt-1">{r.owner_email} · {new Date(r.ran_at).toLocaleString()} · <span className={r.status === "completed" ? "text-emerald-600" : "text-amber-600"}>{r.status}</span></div>
                  </Link>
                )) : <div className="text-xs text-slate-400">No team-visible runs yet. Run a workflow and set its visibility to <strong>team</strong> to share with this workspace.</div>}
              </div>
            </div>

            <div className="rp-card p-6" data-testid="team-shared-assessments">
              <div className="overline flex items-center gap-2"><ClipboardList size={11} /> Team assessments</div>
              <h3 className="font-display text-lg font-semibold mt-3">Shared scores across the workspace.</h3>
              <div className="mt-4 space-y-2">
                {sharedAssessments.length > 0 ? sharedAssessments.map(a => (
                  <div key={a.id} className="p-3 rounded-sm border border-slate-100 flex items-center gap-4" data-testid={`shared-assessment-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{a.title}</div>
                      <div className="text-xs text-slate-500 mt-1">{a.user_email} · {new Date(a.created_at).toLocaleString()}</div>
                    </div>
                    {(a.result?.score ?? a.result?.average) != null && (
                      <div className="font-display text-2xl font-bold flex-shrink-0" style={{ color: "var(--rp-accent)" }}>{a.result?.score ?? a.result?.average}</div>
                    )}
                    {a.result?.overall_level != null && a.result?.score == null && (
                      <div className="font-display text-2xl font-bold flex-shrink-0" style={{ color: "var(--rp-accent)" }}>L{a.result.overall_level}</div>
                    )}
                  </div>
                )) : <div className="text-xs text-slate-400">Nothing shared yet. Open your <Link to="/profile" className="underline" style={{ color: "var(--rp-accent)" }}>profile</Link> and click <strong>Share with team</strong> on any saved assessment.</div>}
              </div>
            </div>

            <div className="rp-card p-6" data-testid="team-shared-searches">
              <div className="overline flex items-center gap-2"><Bookmark size={11} /> Team saved searches</div>
              <h3 className="font-display text-lg font-semibold mt-3">Claude answers your teammates bookmarked.</h3>
              <div className="mt-4 space-y-2">
                {sharedSearches.length > 0 ? sharedSearches.map(s => (
                  <div key={s.id} className="p-3 rounded-sm border border-slate-100" data-testid={`shared-search-${s.id}`}>
                    <div className="text-sm font-medium">"{s.query}"</div>
                    {s.answer && <p className="text-xs text-slate-600 mt-1 leading-relaxed line-clamp-2">{s.answer}</p>}
                    <div className="text-[10px] font-mono text-slate-400 mt-2">{s.user_email} · {new Date(s.created_at).toLocaleString()}</div>
                  </div>
                )) : <div className="text-xs text-slate-400">Nothing shared yet. Save a global search on your <Link to="/profile" className="underline" style={{ color: "var(--rp-accent)" }}>profile</Link> and share it with the team.</div>}
              </div>
            </div>
          </div>

          {/* Members + invite */}
          <aside className="space-y-6">
            <div className="rp-card p-6" data-testid="team-members">
              <div className="overline flex items-center gap-2"><Users size={11} /> Members</div>
              <div className="space-y-2 mt-4">
                {data.members.map(m => (
                  <div key={m.id} className="flex items-center gap-3 p-2 rounded-sm" style={{ background: "var(--rp-bg-subtle)" }} data-testid={`team-member-${m.id}`}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: "var(--rp-accent)", color: "#fff" }}>{(m.name || m.email)[0].toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{m.email}</div>
                      <div className="text-[10px] font-mono text-slate-500 flex items-center gap-1">
                        {m.role === "owner" && <Crown size={9} style={{ color: "var(--rp-accent)" }} />}
                        {m.role}{m.is_pro && " · pro"}
                      </div>
                    </div>
                    {isOwnerOrAdmin && m.role !== "owner" && m.id !== user.id && (
                      <button onClick={() => removeMember(m.id, m.email)} className="text-rose-600 p-1" title="Remove" data-testid={`team-remove-${m.id}`}><Trash2 size={12} /></button>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={leaveOrg} className="text-xs text-rose-600 mt-4 inline-flex items-center gap-1" data-testid="team-leave"><LogOut size={11} /> Leave workspace</button>
            </div>

            {isOwnerOrAdmin && (
              <div className="rp-card p-6" data-testid="team-invite">
                <div className="overline flex items-center gap-2"><Mail size={11} /> Invite teammate</div>
                <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="teammate@acmebank.sg" className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-4" data-testid="invite-email" />
                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-2 font-mono" data-testid="invite-role">
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <button onClick={invite} disabled={inviting || !inviteEmail.trim()} className="btn-accent w-full justify-center mt-3" data-testid="invite-send">{inviting ? "Creating…" : <><Send size={12} /> Send invite</>}</button>
                <p className="text-[10px] text-slate-500 mt-2">Link is copied to your clipboard. Share it via your preferred channel.</p>
              </div>
            )}

            {data.pending_invites?.length > 0 && (
              <div className="rp-card p-6" data-testid="team-pending">
                <div className="overline">Pending invites · {data.pending_invites.length}</div>
                <div className="space-y-2 mt-3">
                  {data.pending_invites.map(inv => (
                    <div key={inv.token} className="flex items-center gap-2 text-xs" data-testid={`pending-${inv.token}`}>
                      <span className="flex-1 truncate">{inv.email}</span>
                      <span className="font-mono text-[10px] text-slate-400">{inv.role}</span>
                      <button onClick={() => { const u = `${window.location.origin}/team/accept/${inv.token}`; navigator.clipboard.writeText(u).then(() => notify.success("Link copied.")); }} className="text-slate-500 underline">copy</button>
                      <button onClick={() => revokeInvite(inv.token)} className="text-rose-600"><X size={11} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div>
      <div className="font-display text-3xl font-bold" style={{ color: "var(--rp-primary)" }}>{v}</div>
      <div className="overline-muted mt-1">{label}</div>
    </div>
  );
}

function NoOrg({ err }) {
  return (
    <div data-testid="page-team-noorg">
      <SEO title="Team workspaces" description="Create or join a Singapore GRC team workspace." path="/team" />
      <section className="section">
        <div className="container-rp max-w-2xl text-center">
          <div className="overline">Team workspaces</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold mt-3">You're not in a team yet.</h1>
          <p className="text-slate-600 mt-4 leading-relaxed">{err}</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/enterprise" className="btn-accent">Enterprise pricing →</Link>
            <Link to="/" className="btn-outline">Back to home</Link>
          </div>
          <div className="rp-card p-6 mt-12 text-left">
            <div className="overline">How team workspaces work</div>
            <ul className="mt-4 space-y-2 text-sm text-slate-600">
              <li className="flex items-start gap-2"><Check size={14} style={{ color: "var(--rp-accent)" }} className="mt-0.5 flex-shrink-0" /> Sign up with a work email (e.g. <code className="text-xs">@bank.com.sg</code>) — the first signup auto-creates the workspace.</li>
              <li className="flex items-start gap-2"><Check size={14} style={{ color: "var(--rp-accent)" }} className="mt-0.5 flex-shrink-0" /> Workspace owner invites teammates by email — invite link copied automatically.</li>
              <li className="flex items-start gap-2"><Check size={14} style={{ color: "var(--rp-accent)" }} className="mt-0.5 flex-shrink-0" /> Share workflow runs across the team. Run any of the 12 templates → set visibility to <strong>team</strong>.</li>
              <li className="flex items-start gap-2"><Check size={14} style={{ color: "var(--rp-accent)" }} className="mt-0.5 flex-shrink-0" /> Org dashboard aggregates CSRS / PDPA / MAS TRM scores across all members.</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

export function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/org/invite/${token}`).then(r => setInv(r.data)).catch(e => setErr(e.response?.data?.detail || "Invite not found."));
  }, [token]);

  const accept = async () => {
    if (!user) { nav(`/login?next=/team/accept/${token}`); return; }
    setBusy(true);
    try {
      await api.post(`/org/invite/${token}/accept`);
      notify.success("Welcome to the team!");
      nav("/team");
    } catch (e) { notify.error(e.response?.data?.detail || "Could not accept."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="page-accept-invite">
      <SEO title="Accept team invite" description="Accept your team invite." path={`/team/accept/${token}`} />
      <section className="section">
        <div className="container-rp max-w-md text-center">
          {err ? (
            <>
              <div className="overline">Invite</div>
              <h1 className="font-display text-3xl font-bold mt-3">{err}</h1>
              <Link to="/" className="btn-outline mt-6 inline-flex">Back home</Link>
            </>
          ) : !inv ? (
            <div className="text-slate-400">Loading invite…</div>
          ) : (
            <>
              <div className="overline">Team invite</div>
              <h1 className="font-display text-4xl font-bold mt-3">Join <span style={{ color: "var(--rp-accent)" }}>{inv.org_name}</span></h1>
              <p className="text-slate-600 mt-3">{inv.invited_by} invited <strong>{inv.email}</strong> as <strong>{inv.role}</strong>.</p>
              {user && user.email?.toLowerCase() !== inv.email.toLowerCase() && (
                <p className="text-xs text-rose-600 mt-3">You're signed in as {user.email}. Please sign in as {inv.email} to accept.</p>
              )}
              <button onClick={accept} disabled={busy || (user && user.email?.toLowerCase() !== inv.email.toLowerCase())} className="btn-accent mt-8" data-testid="accept-invite-btn">{busy ? "Accepting…" : "Accept invite"}</button>
              {!user && <p className="text-xs text-slate-500 mt-4">You'll be asked to sign in or sign up first.</p>}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
